/**
 * @file lib/form-validation-message.ts
 * @description 表單實例儲存失敗（400 VALIDATION_ERROR）時，把後端 Zod issues 轉成可讀的欄位名稱清單。
 *
 * 2026-09-25 回報序49：舊版只提示「部分欄位未通過校驗」，使用者誤以為草稿要填滿全部欄位；
 * 現明確列出是哪幾個欄位格式有誤（草稿不要求必填，提交時才要求）。
 */
import type { FormSchema } from '@zhcb/forms/browser';

/** 顯示的欄位名稱上限（避免訊息過長） */
const MAX_LABELS = 5;

/**
 * 由後端錯誤 details（Zod issues 陣列，path[0] 為欄位 id）找出對應欄位標籤。
 *
 * @param schema  表單 schema（未載入時回空陣列）
 * @param details 後端 errorResponse 的 details
 * @returns 去重後的欄位標籤（最多 MAX_LABELS 個，超出以「等 N 項」收尾）
 */
export function describeInvalidFields(
  schema: FormSchema | null | undefined,
  details: unknown,
): string[] {
  if (!schema || !Array.isArray(details)) return [];
  const labelById = new Map<string, string>();
  for (const section of schema.sections ?? []) {
    for (const f of section.fields) labelById.set(f.id, f.label);
  }
  const labels: string[] = [];
  for (const issue of details) {
    const path = (issue as { path?: unknown[] } | null)?.path;
    const fieldId = Array.isArray(path) ? path[0] : undefined;
    if (typeof fieldId !== 'string') continue;
    const label = labelById.get(fieldId) ?? fieldId;
    if (!labels.includes(label)) labels.push(label);
  }
  if (labels.length <= MAX_LABELS) return labels;
  return [...labels.slice(0, MAX_LABELS), `等 ${labels.length} 項`];
}
